import React, { useState, useEffect } from 'react';
import { useStore } from '../../store';

const COOKIE_KEY = 'mm_cookie_consent';

type ConsentState = 'pending' | 'accepted' | 'declined';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// ── GA4 helpers ──────────────────────────────────────────────
// Replace G-LPJ03LZVDZ with your real GA4 Measurement ID
const GA_ID = 'G-LPJ03LZVDZ';

function loadGA() {
  if (document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer!.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    anonymize_ip: true,
    allow_google_signals: true,    // enables remarketing / retargeting
    allow_ad_personalization_signals: true,
  });
}

function disableGA() {
  // Signal GA to not track
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_personalization: 'denied',
    });
  }
  // Set opt-out property
  (window as any)[`ga-disable-${GA_ID}`] = true;
}

// ── Component ─────────────────────────────────────────────────
export const CookieBanner: React.FC = () => {
  const { lang } = useStore();
  const [consent, setConsent] = useState<ConsentState>('pending');
  const [visible, setVisible] = useState(false);
  const [details, setDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY) as ConsentState | null;
    if (stored === 'accepted') {
      setConsent('accepted');
      loadGA();
    } else if (stored === 'declined') {
      setConsent('declined');
      disableGA();
    } else {
      // Show banner after small delay for UX
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
    loadGA();
  };

  const decline = () => {
    localStorage.setItem(COOKIE_KEY, 'declined');
    setConsent('declined');
    setVisible(false);
    disableGA();
  };

  if (!visible || consent !== 'pending') return null;

  const fr = lang === 'fr';

  return (
    <div
      role="dialog"
      aria-label={fr ? 'Gestion des cookies' : 'Cookie consent'}
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(560px, calc(100vw - 2rem))',
        zIndex: 9000,
        background: 'rgba(6, 11, 24, 0.97)',
        border: '1px solid rgba(79,195,247,0.3)',
        borderRadius: 'var(--radius-xl)',
        backdropFilter: 'blur(24px)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(79,195,247,0.06)',
        padding: '1.4rem 1.6rem',
        animation: 'slideUpBanner 0.4s cubic-bezier(0.22,0.61,0.36,1)',
      }}
    >
      <style>{`
        @keyframes slideUpBanner {
          from { opacity:0; transform:translateX(-50%) translateY(24px); }
          to   { opacity:1; transform:translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '1.1rem' }}>🍪</span>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontSize: '0.95rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--white)',
        }}>
          {fr ? 'Paramètres des cookies' : 'Cookie Settings'}
        </span>
      </div>

      {/* Text */}
      <p style={{ fontSize: '0.8rem', color: 'var(--silver-mid)', lineHeight: 1.65, marginBottom: '0.6rem' }}>
        {fr
          ? 'Nous utilisons Google Analytics et des cookies de remarketing pour améliorer votre expérience et mesurer notre audience. Nous ne vendons pas vos données et n\'utilisons pas votre localisation GPS.'
          : 'We use Google Analytics and remarketing cookies to improve your experience and measure our audience. We do not sell your data and do not use your GPS location.'}
      </p>

      {/* Details toggle */}
      <button
        onClick={() => setDetails(d => !d)}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--cyan)',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.68rem',
          cursor: 'pointer',
          padding: 0,
          letterSpacing: '0.08em',
          marginBottom: details ? '0.75rem' : '1rem',
          opacity: 0.8,
          transition: 'opacity 0.15s',
        }}
      >
        {details
          ? (fr ? '▲ Masquer les détails' : '▲ Hide details')
          : (fr ? '▼ Voir les détails' : '▼ See details')}
      </button>

      {details && (
        <div style={{
          background: 'rgba(79,195,247,0.05)',
          border: '1px solid rgba(79,195,247,0.12)',
          borderRadius: 'var(--radius)',
          padding: '0.85rem',
          marginBottom: '1rem',
          fontSize: '0.75rem',
          color: 'var(--silver-mid)',
          lineHeight: 1.7,
        }}>
          {[
            {
              name: 'Google Analytics (GA4)',
              purpose: fr
                ? 'Mesure d\'audience, pages vues, sources de trafic. Anonymisation IP activée.'
                : 'Audience measurement, page views, traffic sources. IP anonymization enabled.',
              duration: fr ? '26 mois' : '26 months',
              type: fr ? 'Analytique' : 'Analytics',
            },
            {
              name: fr ? 'Remarketing Google' : 'Google Remarketing',
              purpose: fr
                ? 'Publicités pertinentes sur le réseau Google. Basé sur les pages visitées, pas sur vos données personnelles.'
                : 'Relevant ads across Google network. Based on pages visited, not personal data.',
              duration: fr ? '13 mois' : '13 months',
              type: fr ? 'Publicité' : 'Advertising',
            },
          ].map(cookie => (
            <div key={cookie.name} style={{ marginBottom: '0.6rem', paddingBottom: '0.6rem', borderBottom: '1px solid rgba(79,195,247,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.2rem' }}>
                <span style={{ color: 'var(--white)', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}>{cookie.name}</span>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.6rem',
                  color: 'var(--cyan-dim)',
                  background: 'rgba(79,195,247,0.08)',
                  border: '1px solid rgba(79,195,247,0.15)',
                  borderRadius: '4px',
                  padding: '0.1rem 0.4rem',
                  whiteSpace: 'nowrap',
                }}>
                  {cookie.type}
                </span>
              </div>
              <div>{cookie.purpose}</div>
              <div style={{ color: 'var(--silver-dim)', marginTop: '0.15rem' }}>
                {fr ? 'Durée : ' : 'Duration: '}<strong style={{ color: 'var(--silver-mid)' }}>{cookie.duration}</strong>
              </div>
            </div>
          ))}
          <div style={{ marginTop: '0.25rem', fontStyle: 'italic', color: 'var(--silver-dim)' }}>
            {fr
              ? '✓ Pas de vente de données · ✓ Pas de GPS · ✓ Pas de données sensibles'
              : '✓ No data selling · ✓ No GPS · ✓ No sensitive data'}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={accept}
          style={{
            flex: 1,
            minWidth: 130,
            padding: '0.6rem 1rem',
            background: 'linear-gradient(135deg, var(--cyan-dim), var(--cyan))',
            border: '1px solid var(--cyan)',
            borderRadius: 'var(--radius)',
            color: 'var(--navy-deep)',
            fontFamily: 'var(--font-display)',
            fontSize: '0.82rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'box-shadow 0.2s',
            boxShadow: '0 4px 16px rgba(79,195,247,0.3)',
          }}
          onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 6px 24px rgba(79,195,247,0.5)')}
          onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(79,195,247,0.3)')}
        >
          {fr ? 'Accepter' : 'Accept All'}
        </button>
        <button
          onClick={decline}
          style={{
            flex: 1,
            minWidth: 130,
            padding: '0.6rem 1rem',
            background: 'transparent',
            border: '1px solid var(--navy-border)',
            borderRadius: 'var(--radius)',
            color: 'var(--silver-mid)',
            fontFamily: 'var(--font-display)',
            fontSize: '0.82rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--glass-border)';
            e.currentTarget.style.color = 'var(--silver)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--navy-border)';
            e.currentTarget.style.color = 'var(--silver-mid)';
          }}
        >
          {fr ? 'Refuser' : 'Decline'}
        </button>
      </div>

      {/* Privacy link */}
      <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
        <a
          href="/privacy"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.62rem',
            color: 'var(--silver-dim)',
            textDecoration: 'none',
            letterSpacing: '0.06em',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--cyan)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--silver-dim)')}
        >
          {fr ? 'Politique de confidentialité →' : 'Privacy Policy →'}
        </a>
      </div>
    </div>
  );
};
